import { mapState } from 'vuex';
import { between, minValue, maxValue, required } from 'vuelidate/lib/validators';
import { apiCallers } from '@/api/apiRequest';
import { downloadBase64 } from '@/utils/download';
import { formatMoney, randomStr } from '@/modules/utils';
import trendIcons from '@/components/common/trend-icons.vue';
import inputNumber from '@/components/common/input-number.vue';
import maskInput from '@/components/common/mask-input.vue';
import modal from '@/components/common/modal.vue';
import nSlider from '@/components/common/n-slider.vue';
import nSliderVKB from '@/components/common/n-slider-vkb.vue';
import assetHandler from '@/mixins/assetHandler.js';
import scrollHandler from '@/mixins/scrollHandler.js';
import themeHandler from '@/mixins/themeHandler.js';
import trackingHandler from '@/mixins/trackingHandler.js';
import moment from 'moment';

const validPremiumValues = [
  ...new Array(9).fill(0).map((_, k) => (k + 2) * 10000),
  ...new Array(16).fill(0).map((_, k) => 100000 + (k + 1) * 25000),
  ...new Array(10).fill(0).map((_, k) => 500000 + (k + 1) * 50000)
];
const validDestinationAges = new Array(6).fill(0).map((_, k) => (50 + k * 10));
const POLL_INTERVAL = 500;

export default {
  components: {
    trendIcons,
    inputNumber,
    maskInput,
    modal,
    nSlider,
    nSliderVKB
  },

  mixins: [
    assetHandler,
    scrollHandler,
    themeHandler,
    trackingHandler
  ],

  data () {
    return {
      form: {
        age: 40,
        destination_age: 50,
        single_premium: 20000,
        trend: 'neutral'
      },
      lastForm: {},
      debounceTimeoutId: null,
      destinationAges: validDestinationAges,
      validPremiumValues: validPremiumValues,
      premiumItems: validPremiumValues.map(v => ({ text: `${ formatMoney(v) }`, value: v })),
      minAge: 40,
      maxAge: 80,
      minDestinationAge: 50,
      maxDestinationAge: 100,
      pdfJobId: null,
      pdfPollTimer: null,
      result: {},
      showModal: false,
      showOverflow: false,
      showResult: false,
      tooltip: {
        age: 'Welche Altersgruppen können mit dem GenerationenDepot Invest versichert werden? Sie müssen mindestens 40 Jahre und höchstens 80 Jahre alt sein.',
        single_premium: 'Es sind Einzahlungen zwischen mindestens 20.000 Euro und höchstens 1.000.000 Euro möglich.',
        destination_age: 'Geben Sie an, zu welchem Alter das Gesamtguthaben und die Auszahlung bei Ableben berechnet werden soll.',
        trend: 'Die drei beispielhaft gezeigten Kapitalmarktverläufe basieren auf einer konstanten Wertentwicklung von 4,5%, 1,0% und -0,5% p.a. nach Fondskosten. Die abgebildeten Verläufe entsprechen nicht der Vergangenheit, es können keine Prognosen für zukünftige Entwicklungen abgeleitet werden.',
        capital: 'Für das GenerationenDepot Invest sind anfallende Kosten bereits berücksichtigt. Die genannten Beträge zum Gesamtguthaben werden auf Basis der gewählten Kriterien (Alter, Laufzeit, Einzahlung und angenommener Kapitalmarktverlauf) ermittelt. Die drei beispielhaft gezeigten Kapitalmarktverläufe basieren auf einer Wertentwicklung vor Abzug der Kosten der Kapitalanlage von 6 %, 4,5 % und 0 %. Die abgebildeten Verläufe entsprechen nicht der Vergangenheit, es können keine Prognosen für zukünftige Entwicklungen abgeleitet werden. Diese Gesamtleistungen können nicht garantiert werden. Sie enthalten Leistungen, deren Höhe von der Wertentwicklung der renditeoptimierten Kapitalanlage Zukunft und/oder der freien Fonds sowie den für 2026 festgelegten Überschussanteilsätzen abhängt. Die dargestellten Gesamtleistungen gelten nur dann, wenn die unterstellte gleichbleibende jährliche Wertentwicklung und die Kosten der Kapitalanlagen während der gesamten Versicherungsdauer unverändert bleibt.'
      },
      trendItems: ['positiv', 'neutral', 'negativ'],
    };
  },

  computed: {
    ...mapState([
      'appConfigs'
    ]),

    apiRequest () {
      return apiCallers[this.config.app];
    },

    config () {
      return this.appConfigs[this.$options.configIndex] || {};
    },

    currentTheme() {
      return this.config.theme;
    },

    errorMessages() {
      return {
        age: {
          required: `Bitte geben Sie ein Alter zwischen ${ this.minAge } und ${ this.maxAge } Jahren an.`,
          between: `Bitte geben Sie ein Alter zwischen ${ this.minAge } und ${ this.maxAge } Jahren an.`
        }
      };
    }
  },

  mounted () {
    this.showResult = false;
    this.triggerTransitionEvent('performanceRechner', 1, 'parameterKonfigurieren');
  },

  methods: {
    currentYear() {
      return moment().year();
    },
    checkPDF () {
      this.apiRequest
        .get(`${this.config.paths.taskData}${this.pdfJobId}/`, {
          params: {
            anti_cache: randomStr()
          }
        })
        .then(response => {
          if (!response || !response.data) {
            return;
          }

          if (response.data.status === 1) {
            throw new Error('Not finished');
          }

          const pdfData = response.data.status_data.data;
          const fileName = this.getPDFName('GenerationenDepot_Invest.pdf');

          downloadBase64(pdfData, fileName);

          this.pdfPollTimer = null;
          this.pdfJobId = null;
        })
        .catch(() => {
          this.pdfPollTimer = setTimeout(this.checkPDF, POLL_INTERVAL);
        });
    },

    closeModal() {
      this.showModal = false;
    },

    expandResult(e) {
      e.preventDefault();

      if (!this.showResult) {
        this.triggerActionEvent('berechnen (submit)');

        const trackingKeys = [
          'age',
          'destination_age',
          'single_premium',
          'trend',
        ];

        this.triggerValidationEvent(this.$v.form, trackingKeys, this.errorMessages);
      } else {
        this.triggerActionEvent('neuBerechnen (submit)');
      }

      if(this.$v.$invalid) {
        return ;
      }

      this.showResult = !this.showResult;
      this.showOverflow = false;

      if (this.showResult) {
        this.loadData();

        setTimeout(() => {
          this.showOverflow = true;
        }, 800);
      }
    },

    getPDF (e) {
      e.preventDefault();

      this.triggerActionEvent('berechnungSpeichern (submit)');

      if (this.pdfJobId || (this.result.credit == 0 && this.result.fatality == 0)) {
        return false;
      }

      const data = {
        template_name: 'generationendepot',
        theme: this.getPDFTheme(),
        preview: false,
        data: JSON.stringify(Object.assign({ result: this.result }, this.form)),
        anti_cache: randomStr(),
      };

      this.apiRequest
        .get(this.config.paths.pdf, { params: data })
        .then(response => {
          if (response && response.data && response.data.id) {
            this.pdfJobId = response.data.id;
            this.pdfPollTimer = setTimeout(this.checkPDF, POLL_INTERVAL);
          }
        });
    },

    loadData (delay = true) {
      clearTimeout(this.debounceTimeoutId);

      this.debounceTimeoutId = setTimeout(() => {
        if (this.$v.$invalid || !this.apiRequest) {
          return;
        }

        const data = {
          age: this.form.age,
          destination_age: this.form.destination_age,
          single_premium: this.form.single_premium,
          trend: this.form.trend,
          customer: this.getApiDataSource()
        };

        this.apiRequest
          .get(this.config.paths.calc, { params: data })
          .then(response => {
            if (response && response.data) {
              this.result = response.data;
              this.etrackEventStart && this.etrackEventStart('Berechnungen', 'Risikoleben', `${this.config.theme}_schnellrechner_risiko_leben`);
            }
          });
      }, delay ? 500 : 0);
    },

    onContactClick (e) {
      e.preventDefault();

      this.triggerActionEvent('zurBeratung (submit)');

      if (this.config.contact_url) {
        window.open(this.config.contact_url, '_top');
        return false;
      }

      if (this.config.theme === 'fsoel') {
        window.open('https://www.feuersozietaet.de/content/versicherungen/privatkunden/geldanlage/generationen-depot/beraten-anfragen/?id=GENERATIONENDEPOT&etcc_med=Onsite&etcc_cmp=Generationendepotinvest%2DSchnellrechner&etcc_cu=onsite', '_top');
        return false;
      }

      window.open('https://www.vkb.de/content/versicherungen/leben/geldanlage/generationen-depot/beraten-anfragen/?id=GENERATIONENDEPOT&etcc_med=Onsite&etcc_cmp=Generationendepotinvest%2DSchnellrechner&etcc_cu=onsite', '_top');
    },

    setAge (value) {
      const v = value !== ''
        ? parseInt(value.value ? value.value : value, 10)
        : '';

      this.form.age = v;

      if (!this.$v.form.age.$invalid) {
        this.destinationAges = validDestinationAges.filter(_age => _age > this.form.age);
        this.form.destination_age = this.destinationAges[0];
      }
    },

    setDestinationAge (e) {
      const v = parseInt(e.target.value, 10);
      this.form.destination_age = v;
    },

    setPremiumValue (e) {
      const v = parseInt(e.target.value, 10);
      this.form.single_premium = v;
    },

    setPremiumValueFromSlider (value) {
      const v = parseInt(value.value ? value.value : value, 10);
      this.form.single_premium = v;
    },

    onSelectTrend(trend) {
      this.form.trend = trend;
    },

    formatMoney,
  },

  watch: {
    form: {
      deep: true,
      handler (f) {
        if (this.lastForm.age !== f.age ||
          this.lastForm.destination_age !== f.destination_age ||
          this.lastForm.single_premium !== f.single_premium ||
          this.lastForm.trend !== f.trend
        ) {
          this.result.credit = 0;
          this.result.fatality = 0;
          this.showResult = false;
          if (this.currentTheme === 'vrnet') {
            this.onResize();
          }
        }
        this.lastForm = Object.assign({}, f);
      }
    }
  },

  validations: {
    form: {
      age: {
        required,
        between: between(40, 80)
      },

      destination_age: {
        required,
        min: minValue(50),
        max: maxValue(100)
      },

      single_premium: {
        required,
        min: minValue(20000),
        max: maxValue(1000000)
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/apps/generationendepot/logic.js